import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { VideoLessonDialogProps } from '../types';

export function VideoLessonDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
}: VideoLessonDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-3xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white'
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>{title}</DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Sort ID *</Label>
              <Input
                type="number"
                value={formData.sort_id}
                onChange={(e) =>
                  onFormDataChange((prev) => ({
                    ...prev,
                    sort_id: Number.isNaN(Number(e.target.value)) ? 1 : Number(e.target.value),
                  }))
                }
                className={cn(
                  isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value: 'practical' | 'theoritical' | 'other') =>
                  onFormDataChange((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger
                  className={cn(
                    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="practical">Практика</SelectItem>
                  <SelectItem value="theoritical">Теория</SelectItem>
                  <SelectItem value="other">Другое</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Название видеоурока"
              className={cn(
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>URL *</Label>
            <Input
              value={formData.url}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              className={cn(
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Описание</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Описание (необязательно)"
              rows={4}
              className={cn(
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
              )}
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_active ?? true}
              onCheckedChange={(checked) =>
                onFormDataChange((prev) => ({ ...prev, is_active: checked }))
              }
            />
            <div>
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Показывать в приложении
              </Label>
              <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                Выключенный урок остаётся здесь, но пользователи его не видят
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className={cn(
              isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200'
            )}
          >
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
